import { useCallback, useEffect, useState } from 'react'
import type { AppCore } from '@core/bootstrap'
import type { CatalogDiagnostics } from '@core/services/catalog/CatalogService'
import { syncService } from '@core/services/sync/SyncService'
import { PosEvent } from '@core/bus/events'
import { btn, btnBase, btnSize, cx, hint } from '@ui/classes'
import { errBanner, okBanner } from '@ui/admin'
import { useAuthToken } from './useAuthToken'

/**
 * Диагностика каталога устройства (ARCH-TASK-61.0).
 *
 * Отвечает на вопрос «почему на этой кассе N товаров, а на другой M»:
 * активная версия, её источник (облако/локальный импорт), живые строки,
 * уникальные пары «ШК + название» и мягко удалённые.
 *
 * Плюс инструменты починки: пересинк из облака, локальная дедупликация,
 * очистка копий старых версий.
 */
export function CatalogDiagnosticsSection({
  core,
  refreshKey
}: {
  core: AppCore
  refreshKey: number
}) {
  const token = useAuthToken()
  const [data, setData] = useState<CatalogDiagnostics | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const reload = useCallback(async () => {
    try {
      const next = await core.catalog.getCatalogDiagnostics()
      setData(next)
      setError(null)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }, [core.catalog])

  useEffect(() => {
    void reload()
  }, [reload, refreshKey])

  const notifyCatalogChanged = useCallback(
    (versionId: number | null) => {
      core.bus.emit(PosEvent.CATALOG_UPDATED, { versionId: versionId ?? 0, count: 0 })
    },
    [core.bus]
  )

  const runAction = async (action: () => Promise<void>) => {
    setBusy(true)
    setError(null)
    setMessage(null)
    try {
      await action()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const resync = () =>
    runAction(async () => {
      if (!token) return
      await syncService.resyncCatalog(token)
      notifyCatalogChanged(data?.activeVersionId ?? null)
      await reload()
      setMessage('Каталог перечитан из облака')
    })

  const dedupe = () =>
    runAction(async () => {
      const merged = await core.catalog.dedupeActiveVersion()
      notifyCatalogChanged(data?.activeVersionId ?? null)
      await reload()
      setMessage(merged > 0 ? `Объединено дублей: ${merged}` : 'Дублей не найдено')
    })

  const prune = () => {
    const confirmed = window.confirm(
      'Удалить локальные копии старых версий? Локальный откат к ним станет недоступен.'
    )
    if (!confirmed) return
    return runAction(async () => {
      const removed = await core.catalog.pruneOldVersions()
      await reload()
      setMessage(`Удалено строк старых версий: ${removed}`)
    })
  }

  if (error && !data) {
    return (
      <div>
        <p className={errBanner}>{error}</p>
      </div>
    )
  }
  if (!data) {
    return null
  }

  const sourceLabel =
    data.activeVersionSource === 'cloud'
      ? 'облако'
      : data.activeVersionSource === 'local'
        ? 'локальный импорт'
        : 'неизвестен'
  const hasDuplicates = data.activeLive !== data.activeUnique
  const notCloud = data.activeVersionSource !== 'cloud'

  return (
    <div>
      {message && <p className={okBanner}>{message}</p>}
      {error && <p className={errBanner}>{error}</p>}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Metric label="Активная версия" value={data.activeVersionName ?? '—'} />
        <Metric label="Источник" value={sourceLabel} />
        <Metric label="Живых строк" value={String(data.activeLive)} />
        <Metric label="Уникальных" value={String(data.activeUnique)} />
        <Metric label="Удалённых" value={String(data.activeDeleted)} />
        <Metric label="Строк в версии" value={String(data.activeRows)} />
        <Metric label="Версий локально" value={String(data.versions)} />
        <Metric label="Строк в БД" value={String(data.totalRows)} />
      </div>

      {notCloud && (
        <p className={errBanner}>
          Активна не облачная версия: касса показывает локальный каталог. Переключитесь на
          «Версия N (Облако)» в истории версий.
        </p>
      )}
      {hasDuplicates && (
        <p className={hint}>
          В активной версии есть дубли: живых строк {data.activeLive}, уникальных пар{' '}
          {data.activeUnique}. Нажмите «Объединить дубли».
        </p>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          onClick={() => void resync()}
          disabled={busy || !token}
          className={cx(btnBase, btn.secondary, btnSize.sm)}
        >
          Пересинхронизировать каталог
        </button>
        <button
          onClick={() => void dedupe()}
          disabled={busy}
          className={cx(btnBase, btn.secondary, btnSize.sm)}
        >
          Объединить дубли
        </button>
        <button
          onClick={() => void prune()}
          disabled={busy || notCloud}
          title={
            notCloud
              ? 'Доступно только когда активна облачная версия каталога'
              : 'Удалить локальные копии прежних версий'
          }
          className={cx(btnBase, btn.secondary, btnSize.sm)}
        >
          Очистить старые версии
        </button>
      </div>
      <p className={hint}>
        «Очистить старые версии» удаляет локальные копии прежних версий: база сжимается
        с нескольких тысяч строк до размера каталога. Локальный откат к старым версиям
        после этого недоступен (серверный откат — в ARCH-TASK-61b).
      </p>
    </div>
  )
}

/** Ячейка метрики: подпись + значение. */
function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-micro uppercase tracking-wide text-muted">{label}</div>
      <div className="font-semibold">{value}</div>
    </div>
  )
}
